"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { CampaignPageSection } from "@growwithai/shared";
import SafeImage from "@/components/common/SafeImage";
import { formatRupiah } from "@/lib/utils";
import { useProductInfo, durationLabel, type SimpleProductInfo } from "./useProductInfo";
import EmbeddedOrderForm from "./EmbeddedOrderForm";
import { sectionBgStyle, sectionHeadingStyle } from "../section-style";
type ProductOfferSectionData = Extract<CampaignPageSection, {
  type: "product_offer";
}>;

/** Halaman /order/[slug] mendeteksi membership lewat heuristik slug (bukan
 *  kind eksplisit) — prefix "membership-" adalah SATU-SATUNYA pola yang selalu
 *  dikenali terlepas dari slug plan aslinya (slug lain seperti "monthly"/
 *  "yearly"/"lifetime" persis juga dikenali, tapi plan custom seperti
 *  "paket-emas" TIDAK akan cocok tanpa prefix ini). Redirect mode harus tetap
 *  sampai ke halaman order yang benar apa pun slug asli plan-nya. */
function orderHref(kind: ProductOfferSectionData["product"]["kind"], slug: string): string {
  return kind === "membership" ? `/order/membership-${slug}` : `/order/${slug}`;
}
function ProductSummary(t0) {
  const $ = _c(21);
  const {
    product,
    size
  } = t0;
  let t1;
  if ($[0] !== product.duration_type) {
    t1 = durationLabel(product.duration_type);
    $[0] = product.duration_type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const duration = t1;
  const t2 = size === "lg" ? "min-w-0 flex-1 text-center sm:text-left" : "min-w-0 text-center sm:text-left";
  let t3;
  if ($[2] !== product.title) {
    t3 = <h3 className="text-lg font-bold text-warm-black">{product.title}</h3>;
    $[2] = product.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== product.price) {
    t4 = formatRupiah(product.price);
    $[4] = product.price;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span className="text-xl font-bold text-[var(--brand-primary)]">{t4}</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== product.compare_at_price || $[9] !== product.price) {
    t6 = product.compare_at_price && product.compare_at_price > product.price && <span className="text-sm text-gray-400 line-through">{formatRupiah(product.compare_at_price)}</span>;
    $[8] = product.compare_at_price;
    $[9] = product.price;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="mt-1.5 flex items-center justify-center gap-2 sm:justify-start">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== duration) {
    t8 = duration && <p className="mt-0.5 text-xs text-gray-500">{duration}</p>;
    $[14] = duration;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className={t2}>{t3}{t7}{t8}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
export default function ProductOfferSection(t0) {
  const $ = _c(16);
  const {
    section
  } = t0;
  const {
    product,
    loading
  } = useProductInfo(section.product.kind, section.product.slug);
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <section className="mx-auto max-w-3xl px-5 py-10"><div className="h-40 animate-pulse rounded-2xl bg-gray-100" /></section>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!product) {
    return null;
  }
  let t1;
  if ($[1] !== section.custom_style) {
    t1 = sectionBgStyle(section.custom_style);
    $[1] = section.custom_style;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== section.custom_style || $[4] !== section.heading) {
    t2 = section.heading && <h2 className="mb-5 text-center text-2xl font-bold text-warm-black" style={sectionHeadingStyle(section.custom_style)}>{section.heading}</h2>;
    $[3] = section.custom_style;
    $[4] = section.heading;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== product || $[7] !== section.button_label || $[8] !== section.mode || $[9] !== section.product.kind || $[10] !== section.product.slug) {
    t3 = section.mode === "embedded_form" ? <div className="space-y-5"><div className="flex flex-col items-center gap-4 sm:flex-row">{product.thumbnail_url && <SafeImage src={product.thumbnail_url} alt={product.title} className="h-24 w-24 shrink-0 rounded-xl object-cover" />}<ProductSummary product={product} size="sm" /></div><EmbeddedOrderForm product={product} kind={section.product.kind} slug={section.product.slug} /></div> : <div className="flex flex-col items-center gap-5 rounded-2xl border border-black/[0.08] bg-white p-6 shadow-sm sm:flex-row">{product.thumbnail_url && <SafeImage src={product.thumbnail_url} alt={product.title} className="h-32 w-32 shrink-0 rounded-xl object-cover" />}<ProductSummary product={product} size="lg" /><Link href={orderHref(section.product.kind, section.product.slug)} className="inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-[var(--brand-primary)] px-6 text-sm font-semibold text-[var(--brand-on-primary,#fff)] shadow-sm transition-colors hover:bg-[var(--brand-primary-hover)] sm:w-auto">{section.button_label}</Link></div>;
    $[6] = product;
    $[7] = section.button_label;
    $[8] = section.mode;
    $[9] = section.product.kind;
    $[10] = section.product.slug;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <section className="mx-auto max-w-3xl px-5 py-10" style={t1}>{t2}{t3}</section>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}